<template>
  <main class="container py-5">

    <!-- =========================
         PAGE HEADER
    ========================== -->

    <div
      class="page-header
             d-flex
             justify-content-between
             align-items-center
             flex-wrap
             gap-3
             mb-4"
    >

      <div>

        <span class="section-label">
          SHOPPING CART
        </span>

        <h2 class="cart-title">
          Your Cart
        </h2>

        <p class="cart-subtitle">
          Review your selected books
          before checkout.
        </p>

      </div>


      <!-- Items Counter -->

      <div class="items-counter">

        <span class="items-icon">
          🛒
        </span>

        <div class="items-counter-text">

          <span class="items-number">
            {{ cartCount }}
          </span>

          <span class="items-word">
            {{ cartCount === 1
              ? 'item'
              : 'items'
            }}
          </span>

        </div>

      </div>

    </div>


    <!-- =========================
         EMPTY CART
    ========================== -->

    <div
      v-if="cart.length === 0"
      class="empty-cart"
    >

      <div class="empty-icon">
        🛒
      </div>

      <h3 class="empty-title">
        Your cart is empty
      </h3>

      <p class="empty-text">
        Discover a book and add it
        to your shopping cart.
      </p>

      <router-link
        to="/books"
        class="btn btn-primary"
      >
        Browse Books
      </router-link>

    </div>


    <!-- =========================
         CART CONTENT
    ========================== -->

    <div
      v-else
      class="row g-4"
    >

      <!-- PRODUCTS -->

      <div class="col-lg-8">

        <div
          v-for="item in cart"
          :key="item.ISBN"
          class="cart-item"
        >

          <div
            class="row g-0 align-items-center"
          >

            <!-- Image -->

            <div class="col-md-3">

              <img
                :src="item.image"
                :alt="item.name"
                class="cart-image"
              >

            </div>


            <!-- Information -->

            <div class="col-md-9">

              <div class="cart-content">

                <div
                  class="
                    d-flex
                    justify-content-between
                    align-items-start
                  "
                >

                  <div>

                    <h5 class="product-title">
                      {{ item.name }}
                    </h5>

                    <p class="product-author">
                      {{ item.author }}
                    </p>

                  </div>


                  <button
                    class="remove-btn"
                    @click="
                      removeItem(
                        item.ISBN
                      )
                    "
                  >
                    ×
                  </button>

                </div>


                <!-- Product bottom -->

                <div
                  class="product-bottom"
                >

                  <div
                    class="price-section"
                  >

                    <span
                      class="price-label"
                    >
                      PRICE
                    </span>

                    <strong
                      class="product-price"
                    >
                      {{
                        formatPrice(
                          item.price
                        )
                      }}
                    </strong>

                  </div>


                  <!-- Quantity -->

                  <div
                    class="quantity-section"
                  >

                    <span
                      class="quantity-label"
                    >
                      QUANTITY
                    </span>

                    <div
                      class="quantity-control"
                    >

                      <button
                        class="quantity-btn"
                        @click="
                          decrease(
                            item.ISBN
                          )
                        "
                      >
                        −
                      </button>

                      <span
                        class="quantity-number"
                      >
                        {{ item.quantity }}
                      </span>

                      <button
                        class="quantity-btn"
                        @click="
                          increase(
                            item.ISBN
                          )
                        "
                      >
                        +
                      </button>

                    </div>

                  </div>


                  <!-- Item total -->

                  <div
                    class="item-total-section"
                  >

                    <span
                      class="total-label"
                    >
                      TOTAL
                    </span>

                    <strong
                      class="item-total"
                    >
                      {{
                        formatPrice(
                          item.price *
                          item.quantity
                        )
                      }}
                    </strong>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>

      </div>


      <!-- =========================
           SUMMARY
      ========================== -->

      <div class="col-lg-4">

        <div class="summary-card">

          <h4
            class="summary-title"
          >
            Order Summary
          </h4>


          <div
            class="summary-row"
          >

            <span>
              Items
            </span>

            <strong>
              {{ cartCount }}
            </strong>

          </div>


          <div
            class="summary-row"
          >

            <span>
              Subtotal
            </span>

            <strong>
              {{
                formatPrice(
                  subtotal
                )
              }}
            </strong>

          </div>


          <div
            class="summary-row"
          >

            <span>
              Shipping
            </span>

            <strong
              :class="{
                'free-shipping':
                  shipping === 0
              }"
            >
              {{
                shipping === 0
                  ? 'FREE'
                  : formatPrice(
                      shipping
                    )
              }}
            </strong>

          </div>


          <div class="summary-divider"></div>


          <div
            class="
              summary-row
              total-row
            "
          >

            <span>
              Total
            </span>

            <strong>
              {{
                formatPrice(
                  total
                )
              }}
            </strong>

          </div>


          <router-link
            to="/checkout"
            class="checkout-btn"
          >

            Proceed to Checkout

            <span>
              →
            </span>

          </router-link>

        </div>

      </div>

    </div>

  </main>
</template>

<script>
export default {
  name: 'Cart',

  computed: {

    cart() {
      return this.$store.state.cart
    },

    cartCount() {
      return this.$store
        .getters.cartCount
    },

    subtotal() {
      return this.$store
        .getters.cartSubtotal
    },

    shipping() {
      return this.$store
        .getters.shippingCost
    },

    total() {
      return this.$store
        .getters.cartTotal
    }
  },

  methods: {

    formatPrice(value) {
      return new Intl.NumberFormat(
        'en-SA',
        {
          style: 'currency',
          currency: 'SAR'
        }
      ).format(value)
    },

    increase(ISBN) {
      this.$store.dispatch(
        'increaseCartItem',
        ISBN
      )
    },

    decrease(ISBN) {
      this.$store.dispatch(
        'decreaseCartItem',
        ISBN
      )
    },

    removeItem(ISBN) {
      this.$store.dispatch(
        'removeFromCart',
        ISBN
      )
    }
  }
}
</script>

<style scoped>

/* =====================================
   PAGE HEADER
===================================== */

.section-label {
  display:
    block;

  margin-bottom:
    6px;

  color:
    var(--primary);

  font-size:
    0.73rem;

  font-weight:
    800;

  letter-spacing:
    1.8px;
}

.cart-title {
  margin:
    0;

  color:
    var(--text);

  font-size:
    2rem;

  font-weight:
    800;
}

.cart-subtitle {
  margin:
    7px 0 0;

  color:
    var(--text-secondary);
}


/* =====================================
   ITEMS COUNTER
===================================== */

.items-counter {
  display:
    flex;

  align-items:
    center;

  gap:
    11px;

  min-width:
    145px;

  padding:
    11px 17px;

  border-radius:
    16px;

  background:
    linear-gradient(
      135deg,
      #6c3bff,
      #8b5cf6
    );

  color:
    #ffffff;

  box-shadow:
    0 9px 24px
    rgba(
      108,
      59,
      255,
      0.22
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.2
    );
}

.items-icon {
  width:
    40px;

  height:
    40px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  border-radius:
    12px;

  background:
    rgba(
      255,
      255,
      255,
      0.16
    );

  font-size:
    1.1rem;
}

.items-counter-text {
  display:
    flex;

  align-items:
    baseline;

  gap:
    5px;
}

.items-number {
  color:
    #ffffff;

  font-size:
    1.2rem;

  font-weight:
    800;
}

.items-word {
  color:
    rgba(
      255,
      255,
      255,
      0.88
    );

  font-size:
    0.84rem;

  font-weight:
    600;
}


/* =====================================
   CART ITEM
===================================== */

.cart-item {
  margin-bottom:
    16px;

  overflow:
    hidden;

  border:
    1px solid var(--border);

  border-radius:
    18px;

  background:
    var(--surface);

  box-shadow:
    0 8px 25px
    rgba(
      108,
      59,
      255,
      0.06
    );

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.cart-item:hover {
  transform:
    translateY(-2px);

  box-shadow:
    0 14px 32px
    rgba(
      108,
      59,
      255,
      0.11
    );
}

.cart-image {
  width:
    100%;

  height:
    190px;

  object-fit:
    cover;
}

.cart-content {
  padding:
    25px;
}

.product-title {
  margin:
    0 0 5px;

  color:
    var(--text);

  font-weight:
    800;
}

.product-author {
  margin:
    0;

  color:
    var(--text-secondary);

  font-size:
    0.88rem;
}

.remove-btn {
  width:
    32px;

  height:
    32px;

  border:
    1px solid
    rgba(
      239,
      68,
      68,
      0.3
    );

  background:
    transparent;

  color:
    #ef4444;

  border-radius:
    8px;

  font-size:
    1.3rem;

  cursor:
    pointer;
}

.remove-btn:hover {
  background:
    #ef4444;

  color:
    #ffffff;
}


/* =====================================
   PRODUCT BOTTOM
===================================== */

.product-bottom {
  display:
    flex;

  align-items:
    flex-end;

  justify-content:
    space-between;

  gap:
    20px;

  margin-top:
    25px;

  padding-top:
    18px;

  border-top:
    1px solid var(--border);
}

.price-section,
.quantity-section,
.item-total-section {
  display:
    flex;

  flex-direction:
    column;

  gap:
    5px;
}

.price-label,
.quantity-label,
.total-label {
  color:
    var(--text-secondary);

  font-size:
    0.7rem;

  font-weight:
    700;

  letter-spacing:
    0.7px;
}

.product-price {
  color:
    var(--primary);

  font-size:
    0.95rem;

  font-weight:
    800;
}

.item-total {
  color:
    var(--text);

  font-size:
    1rem;

  font-weight:
    800;
}


/* =====================================
   QUANTITY
===================================== */

.quantity-control {
  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  gap:
    10px;

  min-width:
    112px;

  padding:
    5px 7px;

  border:
    1px solid
    #e4daf8;

  border-radius:
    50px;

  background:
    #f2edff;
}

.quantity-btn {
  width:
    31px;

  height:
    31px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  border:
    none;

  border-radius:
    50%;

  background:
    #ffffff;

  color:
    var(--primary);

  font-size:
    1rem;

  font-weight:
    800;

  cursor:
    pointer;
}

.quantity-btn:hover {
  background:
    var(--primary);

  color:
    #ffffff;

  transform:
    scale(1.06);
}

.quantity-number {
  min-width:
    28px;

  display:
    inline-flex;

  justify-content:
    center;

  align-items:
    center;

  color:
    #2b1b44 !important;

  font-size:
    0.95rem;

  font-weight:
    800;

  text-align:
    center;
}


/* =====================================
   SUMMARY
===================================== */

.summary-card {
  position:
    sticky;

  top:
    100px;

  padding:
    28px;

  border:
    1px solid var(--border);

  border-radius:
    18px;

  background:
    var(--surface);

  box-shadow:
    0 9px 30px
    rgba(
      108,
      59,
      255,
      0.08
    );
}

.summary-title {
  margin-bottom:
    25px;

  color:
    var(--text);

  font-weight:
    800;
}

.summary-row {
  display:
    flex;

  justify-content:
    space-between;

  align-items:
    center;

  margin-bottom:
    16px;

  color:
    var(--text-secondary);
}

.summary-row strong {
  color:
    var(--text);
}

.summary-divider {
  height:
    1px;

  margin:
    20px 0;

  background:
    var(--border);
}

.total-row {
  margin-bottom:
    0;

  font-size:
    1.2rem;
}

.total-row strong {
  color:
    var(--primary);
}

.free-shipping {
  color:
    var(--success) !important;
}

.checkout-btn {
  display:
    flex;

  justify-content:
    center;

  align-items:
    center;

  gap:
    10px;

  width:
    100%;

  margin-top:
    25px;

  padding:
    14px 18px;

  border-radius:
    11px;

  background:
    linear-gradient(
      135deg,
      #6c3bff,
      #8b5cf6
    );

  color:
    #ffffff;

  text-decoration:
    none;

  font-weight:
    700;

  box-shadow:
    0 9px 22px
    rgba(
      108,
      59,
      255,
      0.22
    );
}

.checkout-btn:hover {
  color:
    #ffffff;

  transform:
    translateY(-2px);

  box-shadow:
    0 13px 28px
    rgba(
      108,
      59,
      255,
      0.3
    );
}


/* =====================================
   EMPTY CART
===================================== */

.empty-cart {
  padding:
    90px 20px;

  border:
    1px solid var(--border);

  border-radius:
    20px;

  text-align:
    center;

  background:
    var(--surface);

  box-shadow:
    var(--shadow);
}

.empty-icon {
  margin-bottom:
    15px;

  font-size:
    4rem;
}

.empty-title {
  color:
    var(--text);

  font-weight:
    800;
}

.empty-text {
  color:
    var(--text-secondary);

  margin-bottom:
    25px;
}


/* =====================================
   DARK MODE
===================================== */

:global(.dark-mode)
.items-counter {
  background:
    linear-gradient(
      135deg,
      #6c3bff,
      #8b5cf6
    );

  box-shadow:
    0 10px 25px
    rgba(
      0,
      0,
      0,
      0.35
    );
}

:global(.dark-mode)
.cart-item {
  background:
    #181225;

  border-color:
    #34294a;
}

:global(.dark-mode)
.product-title {
  color:
    #f5f3ff;
}

:global(.dark-mode)
.product-author {
  color:
    #b9b0c9;
}

:global(.dark-mode)
.price-label,
:global(.dark-mode)
.quantity-label,
:global(.dark-mode)
.total-label {
  color:
    #9f95af;
}

:global(.dark-mode)
.product-price {
  color:
    #c4b5fd;
}

:global(.dark-mode)
.item-total {
  color:
    #f5f3ff;
}

:global(.dark-mode)
.quantity-control {
  background:
    #211936;

  border-color:
    #443458;
}

:global(.dark-mode)
.quantity-btn {
  background:
    #181225;

  color:
    #c4b5fd;

  border:
    1px solid #49395f;
}

:global(.dark-mode)
.quantity-number {
  color:
    #ffffff !important;
}

:global(.dark-mode)
.summary-card {
  background:
    #181225;

  border-color:
    #34294a;
}

:global(.dark-mode)
.summary-title {
  color:
    #f5f3ff;
}

:global(.dark-mode)
.summary-row {
  color:
    #b9b0c9;
}

:global(.dark-mode)
.summary-row strong {
  color:
    #f5f3ff;
}

:global(.dark-mode)
.total-row strong {
  color:
    #c4b5fd;
}

:global(.dark-mode)
.empty-cart {
  background:
    #181225;

  border-color:
    #34294a;
}

:global(.dark-mode)
.empty-title {
  color:
    #f5f3ff;
}

:global(.dark-mode)
.empty-text {
  color:
    #b9b0c9;
}


/* =====================================
   RESPONSIVE
===================================== */

@media (max-width: 768px) {

  .cart-image {
    height:
      220px;
  }

  .product-bottom {
    flex-direction:
      column;

    align-items:
      flex-start;

    gap:
      15px;
  }

  .quantity-section {
    width:
      100%;
  }

  .summary-card {
    position:
      static;
  }
}

@media (max-width: 576px) {

  .items-counter {
    width:
      100%;

    justify-content:
      center;
  }

  .cart-content {
    padding:
      20px;
  }
}
</style>